import React from 'react';
import { observer } from 'mobx-react';
import {
	ListPanel,
	NotMovingPanel,
	TrackingBarChart,
	TrackingGridModal,
	TrackingStatusCard,
} from 'modules/dashboard/components';

const METRIC_BADGES = [
	{ key: 'awaitingPickup', label: 'Awaiting Pickup', mod: 'warning' },
	{ key: 'exception', label: 'Exceptions', mod: 'danger' },
];

const notMovingMod = pct => (pct > 50 ? 'danger' : pct > 25 ? 'warning' : 'success');

const DATE_PRESETS = [
	{ key: 'yesterday', label: 'Yesterday' },
	{ key: 'last7days', label: 'Last 7 days' },
	{ key: 'custom', label: 'Custom' },
];

const TrackingDashboardTemplate = observer(({ viewStore }) => {
	const {
		orgType,
		loading,
		error,
		activeStatus,
		statusCounts,
		notMovingPercent,
		warehouseLabel,
		sellerLabel,
		chartData,
		selectedWarehouseName,
		warehouseItems,
		selectedWarehouse,
		sellerItems,
		selectedSeller,
		warehouseNotMoving,
		gridViewOpen,
		datePreset,
		customDateFrom,
		customDateTo,
		carrierItems,
		selectedCarrier,
	} = viewStore;

	const mod = notMovingMod(notMovingPercent);

	return (
		<section className="layout__main__body dashboard-page tracking-dashboard-page">
			<div className="dashboard-page__topbar">
				<div className="metric-row">
					{METRIC_BADGES.map(({ key, label, mod: badgeMod }) => (
						<div
							key={key}
							className={`metric-badge metric-badge--${badgeMod}${activeStatus === key ? ' metric-badge--active' : ''}`}
							onClick={() => viewStore.setActiveStatus(activeStatus === key ? 'total' : key)}
						>
							<span className="metric-badge__value">
								{loading ? '—' : (statusCounts[key] || 0).toLocaleString()}
							</span>
							<span className="metric-badge__label">{label}</span>
						</div>
					))}

					<div className={`metric-badge metric-badge--${mod} metric-badge--not-moving`}>
						<span className="metric-badge__value">{loading ? '—' : `${notMovingPercent}%`}</span>
						<span className="metric-badge__label">Not Moving</span>
					</div>
				</div>

				<button
					className={`header__grid-btn${gridViewOpen ? ' header__grid-btn--active' : ''}`}
					onClick={() => (gridViewOpen ? viewStore.closeGridView() : viewStore.openGridView())}
				>
					{gridViewOpen ? 'Close grid view' : 'Open grid view'}
				</button>
			</div>

			<div className="dashboard-body">
				<div className="chart-card">
					<div>
						<div className="chart-card__breadcrumb">
							{sellerLabel} {' • '} {warehouseLabel}
						</div>
						<div className="chart-card__title">Outbound Shipments</div>
					</div>

					{error && <p className="chart-card__error">{error}</p>}

					{loading ? (
						<div className="chart-card__loading">Loading...</div>
					) : (
						<TrackingBarChart
							data={chartData}
							selectedWarehouse={selectedWarehouseName}
							activeStatus={activeStatus}
						/>
					)}

					<div className="status-cards">
						{['awaitingPickup', 'enRoute', 'onVehicleForDelivery', 'delivered', 'exception', 'total'].map(key => (
							<TrackingStatusCard
								key={key}
								statusKey={key}
								value={statusCounts[key]}
								active={activeStatus === key}
								onClick={() => viewStore.setActiveStatus(key)}
							/>
						))}
					</div>
				</div>

				<div className="filter-col">
					{orgType !== 'Warehouse' && (
						<ListPanel
							title="Warehouses"
							items={warehouseItems}
							selected={selectedWarehouse}
							onSelect={id => viewStore.setSelectedWarehouse(id)}
						/>
					)}

					<div className="list-panel">
						<div className="list-panel__header">Shipped Date</div>
						<div className="list-panel__list">
							{DATE_PRESETS.map(({ key, label }) => (
								<div
									key={key}
									className={`list-panel__item${datePreset === key ? ' list-panel__item--selected' : ''}`}
									onClick={() => viewStore.setDatePreset(key)}
								>
									<div className="list-panel__item-left">
										<span className="list-panel__name">{label}</span>
									</div>
								</div>
							))}
						</div>
						{datePreset === 'custom' && (
							<div className="date-panel__custom">
								<input
									type="date"
									className="date-panel__input"
									value={customDateFrom}
									onChange={e => viewStore.setCustomDateFrom(e.target.value)}
								/>
								<span className="date-panel__to">to</span>
								<input
									type="date"
									className="date-panel__input"
									value={customDateTo}
									onChange={e => viewStore.setCustomDateTo(e.target.value)}
								/>
								<button
									className="date-panel__apply"
									onClick={() => viewStore.applyCustomDateRange()}
									disabled={!customDateFrom || !customDateTo}
								>
									Apply
								</button>
							</div>
						)}
					</div>
				</div>

				<div className="filter-col">
					{orgType !== 'Seller' && (
						<ListPanel
							title="Clients"
							items={sellerItems}
							selected={selectedSeller}
							onSelect={id => viewStore.setSelectedSeller(id)}
						/>
					)}

					<ListPanel
						title="Carriers"
						items={carrierItems}
						selected={selectedCarrier}
						onSelect={id => viewStore.setSelectedCarrier(id)}
					/>
				</div>

				<div className="right-column">
					<NotMovingPanel items={warehouseNotMoving} overallPercent={notMovingPercent} />
				</div>
			</div>

			{gridViewOpen && <TrackingGridModal viewStore={viewStore} />}
		</section>
	);
});

export default TrackingDashboardTemplate;